import Logger from '../../../../../ui-kitchen/src/utils/logger';
import {
  updatedEventsData, validateEventsSectionData, updateResyEventItem, updateWithCommonFields,
} from '../AmexExperiential/ExperientialEvents';

import { DATE_TIME_FORMAT, GDAB2B_PAGE_NAME, GDA_PAGE_NAME } from './constants';

const fetchJSONExperientialEvents = async (baseRequestUrl) => {
  const dataEndPoint = `${baseRequestUrl}Experiential/ExperientialEvents.json`;
  try {
    const dataResponse = await fetch(dataEndPoint, {
      method: 'GET',
    });
    const eventsData = await dataResponse.json();
    return eventsData;
  } catch (err) {
    Logger.error('[AmexExperientialUtils] ExperientialEvents JSON File Error :', err);
  }
  return null;
};

const fetchExperientialEvents = async (eventsContent, eventsConfig, baseRequestUrl, {
  selectedCoreSeats, selectedCoreISODate,
  selectedCoreTime, locationBySlug,
}, pageName) => {
  let updatedEvents = [];
  let finalDisplayEvents = [];
  let filteredEvents = [];
  const {
    experiential_events_label_details: eventsTagDetails,
    events_max_number: maxNumber,
    hide_past_events: hidePastEvents,
  } = eventsContent;

  if (Object.keys(locationBySlug).length === 0) return null;
  let eventsData = null;

  try {
    eventsData = await fetchJSONExperientialEvents(baseRequestUrl);
  } catch (err) {
    Logger.error('[AmexExperientialUtils] ExperientialEvents JSON File Error :', err);
  }

  try {
    if (validateEventsSectionData(eventsConfig) && eventsData) {
      const updatedEventsFetch = await updatedEventsData(eventsData, eventsConfig, selectedCoreSeats, selectedCoreISODate,
        selectedCoreTime, locationBySlug);

      // Remove the past events as per the configuration
      updatedEvents = hidePastEvents ? updatedEventsFetch.filter((eventsItem) => (!eventsItem.passed_event)) : updatedEventsFetch;
      finalDisplayEvents = updatedEvents;

      if (eventsTagDetails?.excluded?.length > 0) {
        // remove duplicates
        const nonDuplicatesExcludedTags = eventsTagDetails?.excluded.filter((item,
          index) => eventsTagDetails?.excluded.indexOf(item) === index);

        updatedEvents.forEach((eventItem) => {
          if (eventItem?.event_labels && eventItem?.event_labels.length > 0) {
            // remove duplicates
            const nonDuplicatesEventTags = eventItem?.event_labels?.filter((item,
              index) => eventItem?.event_labels?.indexOf(item) === index);
            let isPresent = false;
            nonDuplicatesExcludedTags.forEach((excludedTag) => {
              nonDuplicatesEventTags.forEach((eventTag) => {
                if (excludedTag === eventTag) {
                  isPresent = true;
                }
              });
            });
            if (!isPresent) filteredEvents.push(eventItem);
          } else {
            filteredEvents.push(eventItem);
          }
        });
        if (eventsTagDetails?.priority?.length === 0) {
          finalDisplayEvents = filteredEvents;
        }
      } else {
        filteredEvents = [...updatedEvents];
      }

      if (eventsTagDetails?.priority?.length > 0) {
        // remove duplicates
        const nonDuplicatesPriorityTags = eventsTagDetails?.priority.filter((item,
          index) => eventsTagDetails?.priority.indexOf(item) === index);

        filteredEvents.forEach((eventItem) => {
          if (eventItem?.event_labels && eventItem?.event_labels.length > 0) {
            // remove duplicates
            const nonDuplicatesEventTags = eventItem?.event_labels?.filter((item,
              index) => eventItem?.event_labels?.indexOf(item) === index);
            let isPresent = false;
            nonDuplicatesPriorityTags.forEach((item) => {
              if (nonDuplicatesEventTags.indexOf(item) !== -1 && !eventItem.priority_index) {
                eventItem.priority_index = 0;
                isPresent = true;
              }
            });
            if (!isPresent) {
              eventItem.priority_index = 10000;
            }
          } else {
            eventItem.priority_index = 10000;
          }
        });

        filteredEvents.sort((a, b) => (a.priority_index - b.priority_index));

        const sortedPriorityEvents = filteredEvents.filter((priorityEvent) => priorityEvent.priority_index === 0)
          .sort((a, b) => (moment(a.event_date_time_est, DATE_TIME_FORMAT)
            .diff(moment(b.event_date_time_est, DATE_TIME_FORMAT))));

        const sortedNonPriorityEvents = filteredEvents.filter((priorityEvent) => priorityEvent.priority_index !== 0)
          .sort((a, b) => (moment(a.event_date_time_est, DATE_TIME_FORMAT)
            .diff(moment(b.event_date_time_est, DATE_TIME_FORMAT))));

        finalDisplayEvents = [...sortedPriorityEvents, ...sortedNonPriorityEvents];
      }
    }
  } catch (err) {
    Logger.error('[AmexExperientialUtils] ExperientialEvents Filtering Logic Error :', err);
  }

  if ((pageName === GDAB2B_PAGE_NAME || pageName === GDA_PAGE_NAME) && !hidePastEvents) {
    const pastEvents = finalDisplayEvents.filter((item) => item.passed_event);
    const nonPastEvents = finalDisplayEvents.filter((item) => !item.passed_event);
    let finalEvents = [];
    if (maxNumber) {
      const nonPastEventsLen = nonPastEvents.length;
      if (!(nonPastEventsLen < maxNumber)) {
        return nonPastEvents.slice(0, maxNumber);
      }
      const pastEventsToAddLen = maxNumber - nonPastEventsLen;
      const pastEventsToAdd = pastEvents.slice(0, pastEventsToAddLen);
      finalEvents = [...nonPastEvents, ...pastEventsToAdd];
      return finalEvents;
    }
    finalEvents = [...nonPastEvents, ...pastEvents];
    return finalEvents;
  }
  if (maxNumber) {
    return finalDisplayEvents.slice(0, maxNumber);
  }
  return finalDisplayEvents;
};

const fetchExperientialData = async (baseRequestUrl) => {
  const dataEndPoint = `${baseRequestUrl}Experiential/ExperientialData.json`;
  try {
    const dataResponse = await fetch(dataEndPoint, {
      method: 'GET',
    });
    const contentData = await dataResponse.json();
    return contentData?.events;
  } catch (err) {
    Logger.error('[AmexExperientialUtils] ExperientialData JSON File Error :', err);
  }
  return null;
};

const updateParsedEventData = (eventDetails, eventsData, contentData,
  jsonExperientialEvents, propsValue) => {
  const {
    selectedCoreSeats, selectedCoreISODate, selectedCoreTime, locationBySlug,
  } = propsValue;

  const {
    cta_label: ctaLabel,
    display_end_date_offset: startDateOffset,
    display_start_date_offset: endDateOffset,
    timezone_mappings: timezoneMappings,
  } = contentData;

  const updatedParsedEventData = [];

  eventsData?.forEach((item) => {
    if (item.event_slug && (item.event_slug === eventDetails.url_slug)) {
      const eventItem = jsonExperientialEvents.RESY_EVENTS.find((element) => (element.event_slug === eventDetails.url_slug
        && element.venue_slug === eventDetails.venue.url_slug));

      const updatedResyEvent = updateResyEventItem(eventItem, eventDetails, locationBySlug,
        timezoneMappings, selectedCoreSeats,
        selectedCoreISODate, selectedCoreTime);
      updateWithCommonFields(updatedResyEvent, ctaLabel, startDateOffset, endDateOffset, timezoneMappings);

      updatedParsedEventData.push(updatedResyEvent);
    } else {
      updatedParsedEventData.push(item);
    }
  });

  return updatedParsedEventData;
};

const fetchEventsAndExperientialExt = async (props, contentData, pageName) => {
  const {
    selectedCoreSeats, selectedCoreISODate, selectedCoreTime,
    locationBySlug,
    ResyAmexConfig: {
      baseRequestUrl,
    },
  } = props;

  if (!contentData || contentData?.hide_section) return null;

  const experientialEventsConfig = await fetchExperientialData(baseRequestUrl);

  try {
    const experientialEvents = await fetchExperientialEvents(contentData?.content,
      experientialEventsConfig,
      baseRequestUrl, {
        selectedCoreSeats,
        selectedCoreISODate,
        selectedCoreTime,
        locationBySlug,
      }, pageName);
    return {
      experientialEvents,
      experientialEventsConfig,
    };
  } catch (err) {
    Logger.error(`[${pageName}] ExperientialData JSON File Error :`, err);
  }
  return null;
};

export {
  fetchExperientialData,
  fetchExperientialEvents,
  updateParsedEventData,
  fetchJSONExperientialEvents,
  fetchEventsAndExperientialExt,
};
